import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Clock, Award, ArrowRight, KeyRound, BookOpen, Users, ShieldCheck } from "lucide-react";

export default function Landing() {
  const [courses, setCourses] = useState([]);

  useEffect(() => {
    api.get("/courses").then((r) => setCourses(r.data)).catch(() => {});
  }, []);

  return (
    <div>
      {/* Hero */}
      <section className="relative bg-marino text-white overflow-hidden">
        <div
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage:
              'url("https://images.unsplash.com/photo-1758691736067-b309ee3ef7b9?crop=entropy&cs=srgb&fm=jpg&w=1600")',
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-6 py-20 lg:py-28 grid lg:grid-cols-2 gap-10 items-center">
          <div className="animate-fade-in">
            <span className="inline-block bg-naranja/20 text-naranja border border-naranja/40 text-xs uppercase tracking-widest px-3 py-1 rounded-full font-semibold mb-4">
              Centro de Entrenamiento Profesional
            </span>
            <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl leading-tight tracking-tighter">
              Certifícate con <span className="text-naranja">OM-PEC</span>
            </h1>
            <p className="mt-3 font-heading text-lg sm:text-xl text-naranja/90 italic font-medium tracking-tight">
              "Protegemos personas, fortalecemos empresas."
            </p>
            <p className="mt-5 text-white/80 text-lg max-w-lg leading-relaxed">
              Formación e-learning automatizada. Aprende a tu ritmo, con evaluaciones diagnósticas,
              contenido protegido y certificación digital al aprobar.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link
                to="/catalog"
                data-testid="explore-courses-btn"
                className="bg-naranja text-white px-6 py-3 rounded-lg font-medium hover:bg-naranjaHover transition-colors inline-flex items-center gap-2 justify-center"
              >
                Explorar cursos <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/redeem"
                data-testid="hero-redeem-btn"
                className="bg-gris text-marino px-6 py-3 rounded-lg font-medium hover:bg-grisHover transition-colors inline-flex items-center gap-2 justify-center"
              >
                <KeyRound className="w-4 h-4" /> Activar mi código
              </Link>
            </div>
          </div>
          <div className="hidden lg:block">
            <div className="bg-white/5 backdrop-blur border border-white/10 rounded-2xl p-6 grid grid-cols-2 gap-4">
              {[
                { icon: BookOpen, k: "150+", v: "Lecciones" },
                { icon: Users, k: "3,200+", v: "Alumnos activos" },
                { icon: Award, k: "94%", v: "Aprobados" },
                { icon: ShieldCheck, k: "100%", v: "Contenido protegido" },
              ].map((s, i) => (
                <div key={i} className="p-4 rounded-xl bg-marino/40 border border-white/10">
                  <s.icon className="w-6 h-6 text-naranja mb-2" strokeWidth={1.75} />
                  <div className="font-heading font-black text-2xl text-white">{s.k}</div>
                  <div className="text-white/70 text-sm">{s.v}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-7xl mx-auto px-6 py-16">
        <h2 className="font-heading font-bold text-marino text-2xl sm:text-3xl lg:text-4xl tracking-tight mb-2">
          ¿Cómo funciona?
        </h2>
        <p className="text-marino/70 mb-10 max-w-2xl">Un flujo académico lineal y controlado que garantiza tu aprendizaje.</p>
        <div className="grid md:grid-cols-4 gap-5">
          {[
            { n: "1", t: "Adquiere tu curso", d: "Compra o activa un código proporcionado por tu empresa." },
            { n: "2", t: "Examen inicial", d: "Diagnóstico obligatorio (1 intento) para desbloquear el contenido." },
            { n: "3", t: "Módulos y lecciones", d: "Videos, textos y recursos. Marca cada lección como completada." },
            { n: "4", t: "Examen final", d: "Hasta 2 intentos. Aprueba con 80% y obtén tu certificado en PDF." },
          ].map((s) => (
            <div key={s.n} className="bg-white border border-gris/30 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
              <div className="w-10 h-10 rounded-full bg-naranja text-white flex items-center justify-center font-heading font-black">{s.n}</div>
              <h3 className="mt-4 font-heading font-bold text-marino text-lg">{s.t}</h3>
              <p className="mt-2 text-marino/70 text-sm leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Courses */}
      <section id="courses" className="max-w-7xl mx-auto px-6 py-16 border-t border-gris/20">
        <div className="flex items-end justify-between mb-8 flex-wrap gap-3">
          <div>
            <h2 className="font-heading font-bold text-marino text-2xl sm:text-3xl lg:text-4xl tracking-tight">
              Cursos destacados
            </h2>
            <p className="text-marino/70 mt-2">Formación profesional para impulsar tu carrera.</p>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {courses.map((c) => (
            <div key={c.id} data-testid={`course-card-${c.id}`} className="bg-white border border-gris/30 rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-shadow group">
              <div className="aspect-video bg-marino/10 relative overflow-hidden">
                {c.thumbnail && (
                  <img src={c.thumbnail} alt={c.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                )}
                <div className="absolute top-3 right-3 bg-marino text-white text-xs font-semibold px-2 py-1 rounded-md">
                  ${c.price}
                </div>
              </div>
              <div className="p-5">
                <h3 className="font-heading font-bold text-marino text-lg leading-snug">{c.title}</h3>
                <p className="mt-2 text-sm text-marino/70 line-clamp-2">{c.description}</p>
                <div className="mt-4 flex items-center justify-between text-sm text-marino/70">
                  <span className="inline-flex items-center gap-1"><Clock className="w-4 h-4" /> {c.duration_hours}h</span>
                  {c.teacher_name && <span className="text-xs">Instructor {c.teacher_name}</span>}
                </div>
                <div className="mt-4 flex gap-2">
                  <Link to={`/courses/${c.id}`} data-testid={`view-course-${c.id}`} className="flex-1 bg-gris text-marino text-center px-4 py-2 rounded-lg text-sm font-medium hover:bg-grisHover transition-colors">
                    Ver detalle
                  </Link>
                  <Link to={`/checkout/${c.id}`} data-testid={`buy-course-${c.id}`} className="flex-1 bg-naranja text-white text-center px-4 py-2 rounded-lg text-sm font-medium hover:bg-naranjaHover transition-colors">
                    Empezar
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
